import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Container, Form } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  FaCheckCircle,
  FaClock,
  FaPlus,
  FaRedoAlt,
  FaSearch,
  FaTrash,
  FaTag,
} from 'react-icons/fa';
import Navigation from './Navigation';
import AdminPageHeader from './AdminPageHeader';
import AdminDataTable from './AdminDataTable';
import useAuth from '../../hooks/useAuth';
import { isManagementUser, readStoredAdminUser } from './adminAccessControl';
import {
  POS_API_BASE_URL,
  buildApiErrorMessage,
  buildAuthHeaders,
  fetchScopedOutlets,
  getInitialScopedOutlets,
  getInitials,
  parseJsonResponse,
  resolveAdminSessionToken,
} from './posBackofficeHelpers';
import './PosCatalogManager.css';

const REFERENCE_CONFIG = {
  metode: {
    path: 'metode',
    title: 'Service Methods',
    eyebrow: 'Catalog & Inventory',
    description: 'Master metode dipakai untuk variasi layanan jasa pada halaman Service Variants.',
    emptyTitle: 'Belum ada metode layanan',
    emptyDescription: 'Tambahkan metode seperti kiloan, satuan, express, atau model layanan lain sesuai kebutuhan outlet.',
    buttonLabel: 'Simpan Metode',
    successLabel: 'Metode berhasil dibuat.',
    deleteLabel: 'Metode berhasil dihapus.',
    fieldLabel: 'Nama metode',
    placeholder: 'Contoh: Cuci Reguler',
    icon: FaTag,
  },
  waktu: {
    path: 'waktu',
    title: 'Service Durations',
    eyebrow: 'Catalog & Inventory',
    description: 'Master durasi dipakai untuk variasi estimasi pengerjaan jasa pada halaman Service Variants.',
    emptyTitle: 'Belum ada durasi layanan',
    emptyDescription: 'Tambahkan pilihan seperti 3 jam, 6 jam, 1 hari, atau 2 hari agar variant layanan konsisten.',
    buttonLabel: 'Simpan Waktu',
    successLabel: 'Waktu berhasil dibuat.',
    deleteLabel: 'Waktu berhasil dihapus.',
    fieldLabel: 'Nama waktu',
    placeholder: 'Contoh: 1 Hari',
    icon: FaClock,
  },
};

function PosLaundryReferenceManager({ referenceType = 'metode' }) {
  const config = REFERENCE_CONFIG[referenceType] || REFERENCE_CONFIG.metode;
  const Icon = config.icon;
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyAndRefreshToken } = useAuth();
  const currentUser = useMemo(() => readStoredAdminUser(), []);
  const canAccess = isManagementUser(currentUser);
  const queryOutletId = useMemo(() => new URLSearchParams(location.search).get('outlet_id') || '', [location.search]);
  const initialOutlets = getInitialScopedOutlets(currentUser);

  const [authToken, setAuthToken] = useState('');
  const [outlets, setOutlets] = useState(initialOutlets);
  const [selectedOutlet, setSelectedOutlet] = useState(queryOutletId || initialOutlets[0]?.id || '');
  const [records, setRecords] = useState([]);
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const selectedOutletName = useMemo(
    () => outlets.find(outlet => outlet.id === selectedOutlet)?.name || 'Belum dipilih',
    [outlets, selectedOutlet]
  );

  const filteredRecords = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return records;
    }

    return records.filter(record =>
      String(record.description || '').toLowerCase().includes(query)
    );
  }, [records, search]);

  const fetchRecords = useCallback(async (token, outletId) => {
    if (!outletId || !POS_API_BASE_URL) {
      setRecords([]);
      return;
    }

    setLoading(true);
    setErrorMessage('');
    try {
      const response = await fetch(
        `${POS_API_BASE_URL}api/${config.path}/?outlet_id=${encodeURIComponent(outletId)}`,
        { headers: buildAuthHeaders(token) }
      );
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(buildApiErrorMessage(response, data, `Gagal memuat ${config.title.toLowerCase()}.`));
      }

      setRecords(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(`Failed to fetch ${config.path}:`, error);
      setRecords([]);
      setErrorMessage(error.message || `Gagal memuat ${config.title.toLowerCase()}.`);
    } finally {
      setLoading(false);
    }
  }, [config.path, config.title]);

  useEffect(() => {
    if (!canAccess) {
      navigate('/dashboardadmin', { replace: true });
      return;
    }

    let active = true;
    const initialize = async () => {
      try {
        const token = await resolveAdminSessionToken(verifyAndRefreshToken, navigate);
        if (!active || !token) {
          return;
        }

        setAuthToken(token);
        const scopedOutlets = await fetchScopedOutlets({ token, currentUser });
        if (!active) {
          return;
        }
        setOutlets(scopedOutlets);
        setSelectedOutlet(prev => prev || queryOutletId || scopedOutlets[0]?.id || '');
      } catch (error) {
        console.error(`Failed to initialize ${config.path}:`, error);
        if (active) {
          setErrorMessage(error.message || `Gagal menyiapkan ${config.title.toLowerCase()}.`);
        }
      }
    };

    initialize();
    return () => {
      active = false;
    };
  }, [canAccess, config.path, config.title, currentUser, navigate, queryOutletId, verifyAndRefreshToken]);

  useEffect(() => {
    if (!authToken || !selectedOutlet) {
      return;
    }

    fetchRecords(authToken, selectedOutlet);
  }, [authToken, fetchRecords, selectedOutlet]);

  const handleSubmit = async event => {
    event.preventDefault();
    if (!selectedOutlet) {
      Swal.fire('Error', 'Pilih outlet lebih dulu.', 'error');
      return;
    }
    if (!description.trim()) {
      Swal.fire('Error', `${config.fieldLabel} wajib diisi.`, 'error');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${POS_API_BASE_URL}api/${config.path}/`, {
        method: 'POST',
        headers: buildAuthHeaders(authToken, {
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          outlet_id: Number(selectedOutlet),
          description: description.trim(),
        }),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(buildApiErrorMessage(response, data, `Gagal menyimpan ${config.title.toLowerCase()}.`));
      }

      setDescription('');
      await fetchRecords(authToken, selectedOutlet);
      Swal.fire('Success', config.successLabel, 'success');
    } catch (error) {
      console.error(`Failed to save ${config.path}:`, error);
      Swal.fire('Error', error.message || `Gagal menyimpan ${config.title.toLowerCase()}.`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = useCallback(async record => {
    const confirmed = await Swal.fire({
      title: 'Hapus data?',
      text: `${record.description || ''} akan dihapus permanen.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Hapus',
      cancelButtonText: 'Batal',
      confirmButtonColor: '#d45463',
    });

    if (!confirmed.isConfirmed) {
      return;
    }

    try {
      const response = await fetch(`${POS_API_BASE_URL}api/${config.path}/${record.id}`, {
        method: 'DELETE',
        headers: buildAuthHeaders(authToken),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(buildApiErrorMessage(response, data, `Gagal menghapus ${config.title.toLowerCase()}.`));
      }

      await fetchRecords(authToken, selectedOutlet);
      Swal.fire('Success', config.deleteLabel, 'success');
    } catch (error) {
      console.error(`Failed to delete ${config.path}:`, error);
      Swal.fire('Error', error.message || `Gagal menghapus ${config.title.toLowerCase()}.`, 'error');
    }
  }, [authToken, config.deleteLabel, config.path, config.title, fetchRecords, selectedOutlet]);

  const columns = useMemo(
    () => [
      {
        name: 'Reference',
        sortable: true,
        grow: 1.5,
        cell: row => (
          <div className="table-identity">
            <span className="table-identity__avatar">
              {getInitials(row.description || 'RF')}
            </span>
            <div className="table-identity__meta">
              <strong>{row.description || '-'}</strong>
              <small>{selectedOutletName}</small>
            </div>
          </div>
        ),
      },
      {
        name: 'Actions',
        width: '140px',
        cell: row => (
          <div className="table-action-group">
            <Button variant="outline-danger" size="sm" onClick={() => handleDelete(row)}>
              <FaTrash /> Hapus
            </Button>
          </div>
        ),
      },
    ],
    [handleDelete, selectedOutletName]
  );

  const headerActions = (
    <>
      <Form.Select
        className="accounting-outlet-picker"
        value={selectedOutlet}
        onChange={event => setSelectedOutlet(event.target.value)}
        disabled={outlets.length === 0}
      >
        {outlets.length === 0 ? <option value="">Belum ada outlet</option> : null}
        {outlets.map(outlet => (
          <option key={outlet.id} value={outlet.id}>
            {outlet.name}
          </option>
        ))}
      </Form.Select>
      <Button
        variant="outline-primary"
        onClick={() => fetchRecords(authToken, selectedOutlet)}
        disabled={!authToken || !selectedOutlet || loading}
      >
        <FaRedoAlt /> Refresh
      </Button>
    </>
  );

  const headerMeta = (
    <>
      <span className="status-chip">
        <FaCheckCircle />
        {filteredRecords.length} data
      </span>
      <span className="status-chip">
        <Icon />
        {selectedOutletName}
      </span>
    </>
  );

  return (
    <>
      <Navigation />
      <Container fluid className="admin-page-shell mt-4 px-4">
        <AdminPageHeader
          icon={Icon}
          eyebrow={config.eyebrow}
          title={config.title}
          description={config.description}
          breadcrumbs={[
            { label: 'Catalog & Inventory' },
            { label: 'Laundry Setup' },
            { label: config.title },
          ]}
          actions={headerActions}
          meta={headerMeta}
        />

        <div className="pos-catalog-shell">
          {!POS_API_BASE_URL ? (
            <Alert variant="danger" className="section-card">
              Endpoint POS belum diatur. Pastikan `REACT_APP_API_PRODUCT_URL` tersedia di konfigurasi `adminfitness`.
            </Alert>
          ) : null}

          {errorMessage ? (
            <Alert variant="warning" className="section-card">
              {errorMessage}
            </Alert>
          ) : null}

          <div className="pos-catalog-layout">
            <section className="surface-card pos-catalog-form-card">
              <div className="pos-catalog-form-head">
                <div>
                  <span className="page-eyebrow">Laundry Setup</span>
                  <h2>Tambah {config.title}</h2>
                  <p>Master ini dipakai langsung oleh halaman Laundry Variants agar pilihan outlet konsisten.</p>
                </div>
              </div>

              <Form onSubmit={handleSubmit}>
                <div className="form-grid">
                  <Form.Group className="field-stack span-12">
                    <Form.Label>{config.fieldLabel}</Form.Label>
                    <Form.Control
                      value={description}
                      onChange={event => setDescription(event.target.value)}
                      placeholder={config.placeholder}
                    />
                  </Form.Group>
                </div>

                <div className="pos-catalog-form-actions mt-3">
                  <Button variant="outline-secondary" type="button" onClick={() => setDescription('')}>
                    Reset
                  </Button>
                  <Button type="submit" disabled={saving || !selectedOutlet || !POS_API_BASE_URL}>
                    <FaPlus />
                    {saving ? 'Menyimpan...' : config.buttonLabel}
                  </Button>
                </div>
              </Form>
            </section>

            <section className="surface-card table-card pos-catalog-table-card">
              <div className="pos-catalog-toolbar">
                <div>
                  <span className="page-eyebrow">Data Reference</span>
                  <h2 className="mb-1">Daftar {config.title}</h2>
                  <p className="text-muted mb-0">Gunakan reference yang jelas agar variant laundry mudah dipilih staf kasir.</p>
                </div>

                <div className="pos-catalog-toolbar__meta">
                  <div className="search-control control-with-icon">
                    <span className="control-with-icon__icon">
                      <FaSearch />
                    </span>
                    <Form.Control
                      value={search}
                      onChange={event => setSearch(event.target.value)}
                      placeholder={`Cari ${config.fieldLabel.toLowerCase()}`}
                    />
                  </div>
                </div>
              </div>

              <AdminDataTable
                columns={columns}
                data={filteredRecords}
                progressPending={loading}
                pagination
                emptyTitle={config.emptyTitle}
                emptyDescription={config.emptyDescription}
              />
            </section>
          </div>
        </div>
      </Container>
    </>
  );
}

export default PosLaundryReferenceManager;
